// @ts-check
// Chamadas da parte financeira do painel de suporte (contrato 08 de cobrança e operação, seção 7.4): visão geral,
// cliente e cobranças do Asaas por instituição, cobranças, notas fiscais e pedidos do site.
import { api } from './api-client.js';

const ADMIN_API = '/admin/api';
const OPTIONS = { skipUnauthorizedRedirect: true };

/**
 * @typedef {{ tenantId: string | null, slug: string, displayName: string, siteOrderId: string | null } | null} PaymentOwner
 * @typedef {{ id: string, customer?: string, subscription?: string | null, value: number, netValue?: number, status: string, dueDate: string,
 *   paymentDate?: string | null, clientPaymentDate?: string | null, billingType?: string, invoiceUrl?: string | null, bankSlipUrl?: string | null,
 *   description?: string | null, externalReference?: string | null, invoiceNumber?: string | null, owner?: PaymentOwner }} AdminPayment
 * @typedef {{ id: string, status: string, customer?: string | null, payment?: string | null, value: number, serviceDescription?: string | null,
 *   pdfUrl?: string | null, xmlUrl?: string | null, number?: string | null, effectiveDate?: string | null, statusDescription?: string | null,
 *   municipalServiceName?: string | null, observations?: string | null, owner?: PaymentOwner }} AdminInvoice
 * @typedef {{ municipalServiceId?: string | null, municipalServiceCode?: string | null, municipalServiceName?: string | null, deductions?: number,
 *   invoiceCreationPeriod?: string, receivedOnly?: boolean, daysBeforeDueDate?: number | null, observations?: string | null,
 *   taxes?: Record<string, number | boolean> }} AdminInvoiceSettings
 * @typedef {{ quantity: number, value: number, netValue: number, partial?: boolean }} PaymentStatistics
 */

/** @param {any} response */
const dataOf = (response) => response.data;

export async function fetchAdminOverview() {
  return dataOf(await api.get(`${ADMIN_API}/overview`, undefined, OPTIONS));
}

/** @param {string} tenantId */
export async function fetchAdminTenantBilling(tenantId) {
  return dataOf(await api.get(`${ADMIN_API}/tenants/${encodeURIComponent(tenantId)}/billing`, undefined, OPTIONS));
}

/**
 * @param {{ status?: string, billingType?: string, dueFrom?: string, dueTo?: string, paidFrom?: string, paidTo?: string, tenantId?: string, offset?: number }} [filters]
 * @returns {Promise<{ payments: AdminPayment[], totalCount: number, hasMore: boolean, offset: number, limit: number, isProviderConfigured: boolean }>}
 */
export async function fetchAdminPayments(filters = {}) {
  const parameters = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== '' && value !== null).map(([key, value]) => [key, String(value)]));
  return dataOf(await api.get(`${ADMIN_API}/payments`, parameters, OPTIONS));
}

/** @param {string} paymentId @returns {Promise<{ payment: AdminPayment, customer: any, invoices: AdminInvoice[] }>} */
export async function fetchAdminPayment(paymentId) {
  return dataOf(await api.get(`${ADMIN_API}/payments/${encodeURIComponent(paymentId)}`, undefined, OPTIONS));
}

/** @param {string} paymentId @param {{ value?: number, description?: string }} input @returns {Promise<{ payment: AdminPayment }>} */
export async function refundAdminPayment(paymentId, input) {
  return dataOf(await api.post(`${ADMIN_API}/payments/${encodeURIComponent(paymentId)}/refund`, input, undefined, OPTIONS));
}

/** @param {string} paymentId */
export async function deleteAdminPayment(paymentId) {
  return dataOf(await api.delete(`${ADMIN_API}/payments/${encodeURIComponent(paymentId)}`, undefined, OPTIONS));
}

/**
 * @param {{ status?: string, from?: string, to?: string, tenantId?: string, offset?: number }} [filters]
 * @returns {Promise<{ invoices: AdminInvoice[], totalCount: number, hasMore: boolean, offset: number, limit: number, isProviderConfigured: boolean }>}
 */
export async function fetchAdminInvoices(filters = {}) {
  const parameters = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== '' && value !== null).map(([key, value]) => [key, String(value)]));
  return dataOf(await api.get(`${ADMIN_API}/invoices`, parameters, OPTIONS));
}

/** @param {string} [description] @returns {Promise<{ services: Array<{ id: string, description: string, issTax: number }>, isProviderConfigured: boolean }>} */
export async function fetchAdminMunicipalServices(description) {
  return dataOf(await api.get(`${ADMIN_API}/invoices/municipal-services`, description ? { description } : undefined, OPTIONS));
}

/**
 * @param {{ paymentId: string, serviceDescription: string, observations?: string, municipalServiceId?: string | null, municipalServiceCode?: string | null,
 *   municipalServiceName: string, value?: number, deductions?: number, effectiveDate?: string, taxes: Record<string, number | boolean>, authorize?: boolean }} input
 * @returns {Promise<{ invoice: AdminInvoice }>}
 */
export async function scheduleAdminInvoice(input) {
  return dataOf(await api.post(`${ADMIN_API}/invoices`, input, undefined, OPTIONS));
}

/** @param {string} invoiceId @returns {Promise<{ invoice: AdminInvoice }>} */
export async function authorizeAdminInvoice(invoiceId) {
  return dataOf(await api.post(`${ADMIN_API}/invoices/${encodeURIComponent(invoiceId)}/authorize`, undefined, undefined, OPTIONS));
}

/** @param {string} invoiceId @returns {Promise<{ invoice: AdminInvoice }>} */
export async function cancelAdminInvoice(invoiceId) {
  return dataOf(await api.post(`${ADMIN_API}/invoices/${encodeURIComponent(invoiceId)}/cancel`, undefined, undefined, OPTIONS));
}

/** @param {string} tenantId @returns {Promise<{ invoiceSettings: AdminInvoiceSettings | null }>} */
export async function fetchAdminInvoiceSettings(tenantId) {
  return dataOf(await api.get(`${ADMIN_API}/tenants/${encodeURIComponent(tenantId)}/invoice-settings`, undefined, OPTIONS));
}

/** @param {string} tenantId @param {Record<string, unknown>} input @returns {Promise<{ invoiceSettings: AdminInvoiceSettings }>} */
export async function saveAdminInvoiceSettings(tenantId, input) {
  return dataOf(await api.put(`${ADMIN_API}/tenants/${encodeURIComponent(tenantId)}/invoice-settings`, input, undefined, OPTIONS));
}

/** @param {string} tenantId */
export async function removeAdminInvoiceSettings(tenantId) {
  return dataOf(await api.delete(`${ADMIN_API}/tenants/${encodeURIComponent(tenantId)}/invoice-settings`, undefined, OPTIONS));
}

/**
 * Exclusão da instituição (3.4.0): o slug digitado confirma; a resposta diz o que foi limpo e o que ficou para repetir.
 * @param {string} tenantId @param {{ confirmSlug: string, reason?: string }} input
 * @returns {Promise<{ tenantId: string, slug: string, displayName: string, providerSubscriptionCanceled: boolean, removedFileCount: number, warnings: string[] }>}
 */
export async function deleteAdminTenant(tenantId, input) {
  return dataOf(await api.post(`${ADMIN_API}/tenants/${encodeURIComponent(tenantId)}/delete`, input, undefined, OPTIONS));
}

/** @param {{ status?: string }} [filters] @returns {Promise<{ orders: any[] }>} */
export async function fetchAdminSiteOrders(filters = {}) {
  return dataOf(await api.get(`${ADMIN_API}/site-orders`, filters.status ? { status: filters.status } : undefined, OPTIONS));
}

/** @param {string} orderId @returns {Promise<{ order: any, tenant: any, payment: AdminPayment | null }>} */
export async function fetchAdminSiteOrder(orderId) {
  return dataOf(await api.get(`${ADMIN_API}/site-orders/${encodeURIComponent(orderId)}`, undefined, OPTIONS));
}

/** @param {string} orderId @returns {Promise<{ contactEmail: string }>} */
export async function resendAdminSiteOrderEmail(orderId) {
  return dataOf(await api.post(`${ADMIN_API}/site-orders/${encodeURIComponent(orderId)}/resend-access-email`, undefined, undefined, OPTIONS));
}
